import React, { useContext, useEffect, useMemo, useState } from "react";
import { CircularProgress, Container, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { DraftYearContext } from "../../contexts/DraftYearContext";
import { publicApi } from "../../api/http";
import PageLoader from "../../components/PageLoader/PageLoader";
import {
    DEFAULT_PLAYER_IMAGE,
    DistributionStrip,
    PickSlotCard,
    ProspectCard,
    computeProspectStats,
    formatProspectName,
} from "../../components/DraftOdds/DraftOddsComponents";
import { buildPlayerPagePath } from "../Player/playerTabs";
import "./DraftOdds.css";

const VIEWS = [
    { id: "picks", label: "By pick" },
    { id: "prospects", label: "By prospect" },
    { id: "leaderboard", label: "Leaderboard" },
];

const PICK_RANGES = [
    { id: "top10", label: "Top 10", min: 1, max: 10 },
    { id: "round1", label: "Round 1", min: 1, max: 32 },
    { id: "round2", label: "Round 2", min: 33, max: 64 },
    { id: "all", label: "All picks", min: 1, max: 64 },
];

const SORT_OPTIONS = {
    picks: [],
    prospects: [
        { id: "consensus", label: "Consensus rank" },
        { id: "expected", label: "Expected pick" },
        { id: "p_first", label: "P(#1 overall)" },
        { id: "p_top5", label: "P(top 5)" },
    ],
    leaderboard: [
        { id: "consensus", label: "Consensus rank" },
        { id: "expected", label: "Expected pick" },
        { id: "p_first", label: "P(#1 overall)" },
        { id: "p_top32", label: "P(round 1)" },
    ],
};

function sortProspects(prospects, sortKey) {
    const items = [...prospects];
    switch (sortKey) {
        case "expected":
            return items.sort((a, b) => a.expected_pick - b.expected_pick);
        case "p_first":
            return items.sort((a, b) => b.p_first - a.p_first);
        case "p_top5":
            return items.sort((a, b) => b.p_top5 - a.p_top5);
        case "p_top32":
            return items.sort((a, b) => computeProspectStats(b).pTop32 - computeProspectStats(a).pTop32);
        default:
            return items.sort((a, b) => a.consensus_ranking - b.consensus_ranking);
    }
}

function DraftOdds() {
    const { draftYear } = useContext(DraftYearContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [draftYearChange, setDraftYearChange] = useState(true);
    const [prevDraftYear, setPrevDraftYear] = useState(null);

    const view = searchParams.get("view") || "picks";
    const pickRange = searchParams.get("range") || "round1";
    const sortKey = searchParams.get("sort") || "consensus";
    const searchQuery = searchParams.get("q") || "";

    useEffect(() => {
        setLoading(true);
        setError(null);
        publicApi
            .get("/draft_odds/board", {
                params: {
                    draft_year: draftYear,
                    pick_count: 64,
                    top_prospects: 64,
                    candidates_per_pick: 5,
                    distribution_depth: 64,
                },
            })
            .then((response) => {
                setPayload(response.data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching draft odds board:", err);
                setPayload(null);
                setError("Unable to load draft odds.");
                setLoading(false);
            });
    }, [draftYear]);

    useEffect(() => {
        if (prevDraftYear !== null && prevDraftYear !== draftYear) {
            setDraftYearChange(false);
        }
        setPrevDraftYear(draftYear);
    }, [draftYear, prevDraftYear]);

    const activeRange = PICK_RANGES.find((range) => range.id === pickRange) || PICK_RANGES[1];

    const filteredPicks = useMemo(() => {
        const picks = payload?.picks || [];
        return picks.filter((slot) => slot.pick >= activeRange.min && slot.pick <= activeRange.max);
    }, [payload, activeRange]);

    const filteredProspects = useMemo(() => {
        const prospects = sortProspects(payload?.prospects || [], sortKey);
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return prospects;
        }
        return prospects.filter((prospect) => formatProspectName(prospect).toLowerCase().includes(query));
    }, [payload, sortKey, searchQuery]);

    const hasData =
        (payload?.picks || []).some((slot) => slot.candidates?.length > 0) ||
        (payload?.prospects || []).length > 0;

    function updateParam(key, value) {
        const next = new URLSearchParams(searchParams);
        if (!value) {
            next.delete(key);
        } else {
            next.set(key, value);
        }
        setSearchParams(next, { replace: true });
    }

    function handleViewChange(nextView) {
        const next = new URLSearchParams(searchParams);
        next.set("view", nextView);
        if (nextView === "picks") {
            next.delete("sort");
            next.delete("q");
        } else if (!next.get("sort")) {
            next.set("sort", "consensus");
        }
        setSearchParams(next, { replace: true });
    }

    return (
        <>
            <Helmet>
                <title>{`${draftYear} Draft Odds - Hockey Pipeline`}</title>
                <meta
                    name="description"
                    content={`Simulated NHL draft odds for the ${draftYear} draft. Explore pick-by-pick probabilities, prospect landing spots, and draft lottery outcomes.`}
                />
                <link rel="canonical" href="https://hockeypipeline.com/draft-odds" />
            </Helmet>

            <PageLoader loading={loading && !draftYearChange} />

            <Container className="draft-odds-page" maxWidth="xl">
                <section className="draft-odds-hero home-panel">
                    <div className="draft-odds-hero-top">
                        <div>
                            <Typography component="p" className="draft-odds-eyebrow">
                                {draftYear} NHL Draft
                            </Typography>
                            <Typography variant="h4" component="h1" className="draft-odds-page-title">
                                Draft Odds
                            </Typography>
                            <Typography variant="body2" component="p" className="draft-odds-page-subtitle">
                                Simulated pick probabilities from consensus rankings — explore who goes where by pick slot, prospect, or full leaderboard.
                            </Typography>
                        </div>
                        <nav className="draft-odds-view-nav" aria-label="Draft odds views">
                            {VIEWS.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`draft-odds-view-chip${view === item.id ? " draft-odds-view-chip--active" : ""}`}
                                    onClick={() => handleViewChange(item.id)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </section>

                {loading && (
                    <div className="draft-odds-page-loading">
                        <CircularProgress size={32} sx={{ color: "var(--brand-strong)" }} />
                    </div>
                )}

                {!loading && error && (
                    <section className="draft-odds-panel home-panel">
                        <Typography variant="body1" className="draft-odds-empty-message">
                            {error}
                        </Typography>
                    </section>
                )}

                {!loading && !error && !hasData && (
                    <section className="draft-odds-panel home-panel">
                        <Typography variant="body1" className="draft-odds-empty-message">
                            Draft odds are not available for {draftYear} yet. Check back after the next simulation run.
                        </Typography>
                    </section>
                )}

                {!loading && !error && hasData && view === "picks" && (
                    <section className="draft-odds-panel home-panel">
                        <div className="draft-odds-toolbar">
                            <Typography variant="overline" className="draft-odds-panel-label">
                                Likely names by pick
                            </Typography>
                            <div className="draft-odds-filter-chips" role="group" aria-label="Pick range">
                                {PICK_RANGES.map((range) => (
                                    <button
                                        key={range.id}
                                        type="button"
                                        className={`draft-odds-filter-chip${pickRange === range.id ? " draft-odds-filter-chip--active" : ""}`}
                                        onClick={() => updateParam("range", range.id)}
                                    >
                                        {range.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="draft-odds-picks-grid">
                            {filteredPicks.map((slot) => (
                                <PickSlotCard key={slot.pick} slot={slot} />
                            ))}
                        </div>
                    </section>
                )}

                {!loading && !error && hasData && view === "prospects" && (
                    <section className="draft-odds-panel home-panel">
                        <div className="draft-odds-toolbar draft-odds-toolbar--split">
                            <Typography variant="overline" className="draft-odds-panel-label">
                                Top prospect landing spots
                            </Typography>
                            <div className="draft-odds-toolbar-controls">
                                <label className="draft-odds-search">
                                    <SearchIcon fontSize="small" />
                                    <input
                                        type="search"
                                        value={searchQuery}
                                        onChange={(event) => updateParam("q", event.target.value)}
                                        placeholder="Search prospects"
                                        aria-label="Search prospects"
                                    />
                                </label>
                                <label className="draft-odds-select-wrap">
                                    <span className="draft-odds-select-label">Sort</span>
                                    <select
                                        value={sortKey}
                                        onChange={(event) => updateParam("sort", event.target.value)}
                                        aria-label="Sort prospects"
                                    >
                                        {SORT_OPTIONS.prospects.map((option) => (
                                            <option key={option.id} value={option.id}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                        {filteredProspects.length === 0 ? (
                            <Typography variant="body2" className="draft-odds-empty-message">
                                No prospects match your search.
                            </Typography>
                        ) : (
                            <div className="draft-odds-prospects-grid">
                                {filteredProspects.map((prospect) => (
                                    <ProspectCard key={prospect.player_id} prospect={prospect} distributionDepth={32} />
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {!loading && !error && hasData && view === "leaderboard" && (
                    <section className="draft-odds-panel home-panel">
                        <div className="draft-odds-toolbar draft-odds-toolbar--split">
                            <Typography variant="overline" className="draft-odds-panel-label">
                                Full odds leaderboard
                            </Typography>
                            <label className="draft-odds-select-wrap">
                                <span className="draft-odds-select-label">Sort</span>
                                <select
                                    value={sortKey}
                                    onChange={(event) => updateParam("sort", event.target.value)}
                                    aria-label="Sort leaderboard"
                                >
                                    {SORT_OPTIONS.leaderboard.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div className="draft-odds-table-wrap">
                            <table className="draft-odds-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Rank</th>
                                        <th scope="col">Player</th>
                                        <th scope="col">Exp.</th>
                                        <th scope="col">P(#1)</th>
                                        <th scope="col">P(top 5)</th>
                                        <th scope="col">P(R1)</th>
                                        <th scope="col">Most likely</th>
                                        <th scope="col">Distribution</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProspects.map((prospect) => {
                                        const stats = computeProspectStats(prospect);
                                        return (
                                            <tr key={prospect.player_id}>
                                                <td className="draft-odds-table-rank">#{prospect.consensus_ranking}</td>
                                                <td>
                                                    <Link
                                                        to={buildPlayerPagePath(prospect.player_id, "draft-odds")}
                                                        className="draft-odds-table-player"
                                                    >
                                                        <img
                                                            src={prospect.player_image_url || DEFAULT_PLAYER_IMAGE}
                                                            alt=""
                                                            className="draft-odds-table-avatar"
                                                            loading="lazy"
                                                        />
                                                        <span>
                                                            <span className="draft-odds-table-name">
                                                                {formatProspectName(prospect)}
                                                            </span>
                                                            <span className="draft-odds-table-meta">{prospect.position}</span>
                                                        </span>
                                                    </Link>
                                                </td>
                                                <td>{prospect.expected_pick.toFixed(1)}</td>
                                                <td>{(prospect.p_first * 100).toFixed(1)}%</td>
                                                <td>{(prospect.p_top5 * 100).toFixed(1)}%</td>
                                                <td>{(stats.pTop32 * 100).toFixed(1)}%</td>
                                                <td>#{prospect.modal_pick}</td>
                                                <td className="draft-odds-table-strip">
                                                    <DistributionStrip
                                                        distribution={prospect.distribution}
                                                        depth={32}
                                                        compact
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </Container>
        </>
    );
}

export default DraftOdds;
